/**
 * Macro preview modal component.
 * Shows recorded actions in human-readable or code view with export capability.
 */

import { generatePythonScript, generateActionSummary } from '../../macro/generator.js';
import { getRecordedActions, subscribeMacro } from '../../state/macro.js';
import { downloadBlob } from '../../utils/download.js';
import { showToast } from './toast.js';

/** Cached API Gateway URL from config.json. */
let _apiGatewayUrl = null;
/** Whether the config fetch has failed (prevents repeated attempts). */
let _configFetchFailed = false;

/**
 * Fetch the API Gateway URL from config.json.
 * Caches the result so subsequent calls return immediately.
 * On failure, sets _configFetchFailed to prevent repeated attempts.
 * @returns {Promise<string|null>} The API Gateway URL or null on failure
 */
async function fetchApiGatewayUrl() {
  if (_apiGatewayUrl) return _apiGatewayUrl;
  if (_configFetchFailed) return null;

  try {
    const response = await fetch('./config.json');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const config = await response.json();
    if (!config.apiGatewayUrl) {
      throw new Error('Missing apiGatewayUrl in config.json');
    }
    _apiGatewayUrl = config.apiGatewayUrl.replace(/\/+$/, '');
    return _apiGatewayUrl;
  } catch (err) {
    console.error('[DEX] Failed to fetch config.json:', err);
    _configFetchFailed = true;
    return null;
  }
}

/**
 * Generate a timestamped filename for the exported script.
 * Format: dex_macro_YYYYMMDD_HHMMSS.py
 * @returns {string} Filename
 */
function generateFilename() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  return `dex_macro_${year}${month}${day}_${hours}${minutes}${seconds}.py`;
}

/**
 * Create the modal HTML structure.
 * @returns {string} Modal HTML
 */
function createModalHtml() {
  return `
    <div id="macro-preview-modal" class="fixed inset-0 z-50 hidden">
      <!-- Backdrop -->
      <div class="absolute inset-0 bg-black/50" data-close></div>

      <!-- Modal content -->
      <div class="absolute inset-4 md:inset-8 bg-white rounded-lg shadow-xl flex flex-col max-h-full overflow-hidden">
        <!-- Header with tabs -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
          <div class="flex items-center gap-4">
            <h2 class="text-lg font-semibold text-gray-900">Macro Preview</h2>
            <div class="flex bg-gray-100 rounded-lg p-1">
              <button id="tab-summary" class="px-3 py-1 text-sm rounded-md bg-white shadow text-gray-900 font-medium transition-all">Summary</button>
              <button id="tab-code" class="px-3 py-1 text-sm rounded-md text-gray-600 hover:text-gray-900 transition-all">Python Code</button>
            </div>
          </div>
          <button data-close class="p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Content area -->
        <div class="flex-1 overflow-auto p-4">
          <div id="preview-summary" class="prose max-w-none">
            <!-- Human-readable action list -->
          </div>
          <div id="preview-code" class="hidden h-full">
            <pre class="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto text-sm font-mono whitespace-pre h-full max-h-[calc(100vh-16rem)]"></pre>
          </div>
        </div>

        <!-- Footer with export -->
        <div class="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 shrink-0">
          <span class="text-sm text-gray-500" id="action-count">0 actions recorded</span>
          <div class="flex items-center gap-2">
            <button data-close class="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              Close
            </button>
            <button id="export-btn" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
              </svg>
              Export Python Script
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Render the summary view content.
 * @param {HTMLElement} container - Summary container element
 */
function renderSummary(container) {
  const actions = getRecordedActions();

  if (actions.length === 0) {
    container.innerHTML = `
      <div class="text-center py-12 text-gray-500">
        <svg class="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path>
        </svg>
        <p class="text-lg font-medium">No actions recorded yet.</p>
        <p class="mt-1">Start recording to capture your workflow.</p>
      </div>
    `;
    return;
  }

  const summaryItems = generateActionSummary();
  container.innerHTML = `
    <div class="space-y-2">
      ${summaryItems.map(item => {
        const isFailed = item.includes('[FAILED');
        return `
          <div class="p-3 rounded-lg ${isFailed ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}">
            <span class="${isFailed ? 'text-red-700' : 'text-gray-700'}">${escapeHtml(item)}</span>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

/**
 * Render the code view content.
 * @param {HTMLElement} container - Code container pre element
 */
function renderCode(container) {
  const actions = getRecordedActions();

  if (actions.length === 0) {
    container.textContent = '# No actions recorded yet.\n# Start recording to capture your workflow.';
    return;
  }

  if (_apiGatewayUrl) {
    const script = generatePythonScript(_apiGatewayUrl);
    container.textContent = script;
  } else {
    container.textContent = '# Unable to load API configuration.\n# Export is disabled until config.json is available.';
  }
}

/**
 * Update the action count display.
 * @param {HTMLElement} countEl - Action count element
 */
function updateActionCount(countEl) {
  const actions = getRecordedActions();
  const count = actions.length;
  countEl.textContent = `${count} action${count === 1 ? '' : 's'} recorded`;
}

/**
 * Escape HTML special characters.
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Initialize the macro preview modal.
 * Listens for 'macro:preview' custom event to show the modal.
 * @returns {Function} Cleanup function
 */
export function initMacroPreview() {
  // Inject modal HTML into body
  const modalContainer = document.createElement('div');
  modalContainer.innerHTML = createModalHtml();
  const modal = modalContainer.firstElementChild;
  document.body.appendChild(modal);

  // Get references to elements
  const summaryTab = modal.querySelector('#tab-summary');
  const codeTab = modal.querySelector('#tab-code');
  const summaryView = modal.querySelector('#preview-summary');
  const codeView = modal.querySelector('#preview-code');
  const codeContent = codeView.querySelector('pre');
  const actionCount = modal.querySelector('#action-count');
  const exportBtn = modal.querySelector('#export-btn');

  // Track current active tab
  let activeTab = 'summary';

  /**
   * Show the modal and populate content.
   * Fetches config.json if not already cached to enable code preview and export.
   */
  async function showModal() {
    // Reset to summary tab
    activeTab = 'summary';
    updateTabs();

    // Fetch API Gateway URL if not already cached
    if (!_apiGatewayUrl && !_configFetchFailed) {
      await fetchApiGatewayUrl();
    }

    // Populate content
    renderSummary(summaryView);
    renderCode(codeContent);
    updateActionCount(actionCount);

    // Update export button state
    const actions = getRecordedActions();
    exportBtn.disabled = actions.length === 0;

    // Show modal
    modal.classList.remove('hidden');
  }

  /**
   * Hide the modal.
   */
  function hideModal() {
    modal.classList.add('hidden');
  }

  /**
   * Update tab styling based on active tab.
   */
  function updateTabs() {
    if (activeTab === 'summary') {
      summaryTab.classList.add('bg-white', 'shadow', 'text-gray-900', 'font-medium');
      summaryTab.classList.remove('text-gray-600');
      codeTab.classList.remove('bg-white', 'shadow', 'text-gray-900', 'font-medium');
      codeTab.classList.add('text-gray-600');
      summaryView.classList.remove('hidden');
      codeView.classList.add('hidden');
    } else {
      codeTab.classList.add('bg-white', 'shadow', 'text-gray-900', 'font-medium');
      codeTab.classList.remove('text-gray-600');
      summaryTab.classList.remove('bg-white', 'shadow', 'text-gray-900', 'font-medium');
      summaryTab.classList.add('text-gray-600');
      codeView.classList.remove('hidden');
      summaryView.classList.add('hidden');
    }
  }

  /**
   * Handle export button click.
   * Fetches config.json if not already cached, then generates and downloads the script.
   */
  async function handleExport() {
    const actions = getRecordedActions();
    if (actions.length === 0) return;

    // Fetch API Gateway URL if not already cached
    if (!_apiGatewayUrl && !_configFetchFailed) {
      await fetchApiGatewayUrl();
    }

    // Block export if config is unavailable
    if (!_apiGatewayUrl) {
      showToast('Cannot export: API configuration not available. Contact your administrator.', 'error');
      return;
    }

    const script = generatePythonScript(_apiGatewayUrl);
    const blob = new Blob([script], { type: 'text/x-python' });
    const filename = generateFilename();

    downloadBlob(blob, filename);
  }

  /**
   * Handle clicks within the modal.
   * @param {Event} event - Click event
   */
  function handleClick(event) {
    const target = event.target;

    // Check for close triggers
    if (target.hasAttribute('data-close') || target.closest('[data-close]')) {
      hideModal();
      return;
    }

    // Check for tab clicks
    if (target.id === 'tab-summary' || target.closest('#tab-summary')) {
      activeTab = 'summary';
      updateTabs();
      return;
    }
    if (target.id === 'tab-code' || target.closest('#tab-code')) {
      activeTab = 'code';
      updateTabs();
      return;
    }

    // Check for export button
    if (target.id === 'export-btn' || target.closest('#export-btn')) {
      handleExport();
      return;
    }
  }

  /**
   * Handle custom event to show preview.
   * @param {CustomEvent} event - macro:preview event
   */
  function handlePreviewEvent(event) {
    showModal();
  }

  /**
   * Handle escape key to close modal.
   * @param {KeyboardEvent} event - Keyboard event
   */
  function handleKeydown(event) {
    if (event.key === 'Escape' && !modal.classList.contains('hidden')) {
      hideModal();
    }
  }

  // Attach event listeners
  modal.addEventListener('click', handleClick);
  document.addEventListener('macro:preview', handlePreviewEvent);
  document.addEventListener('keydown', handleKeydown);

  // Subscribe to macro state changes to update content if modal is open
  const unsubscribe = subscribeMacro(() => {
    if (!modal.classList.contains('hidden')) {
      renderSummary(summaryView);
      renderCode(codeContent);
      updateActionCount(actionCount);

      const actions = getRecordedActions();
      exportBtn.disabled = actions.length === 0;
    }
  });

  // Return cleanup function
  return function cleanup() {
    unsubscribe();
    modal.removeEventListener('click', handleClick);
    document.removeEventListener('macro:preview', handlePreviewEvent);
    document.removeEventListener('keydown', handleKeydown);
    modal.remove();
  };
}
